import axios from 'axios';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';

const API_URL = 'https://api.autocasses.com/api';

const NotificationsScreen = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  const unreadCount = notifications.filter(n => !n.read).length;

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      
      const response = await axios.get(`${API_URL}/notification/usernotif`, { headers });
      
      setNotifications(response.data.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchNotifications();
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('userToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      
      await axios.put(`${API_URL}/notification/${id}/read`, {}, { headers });
      
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === id ? { ...notification, read: true } : notification
        )
      );
    } catch (err) {
      console.log(err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      
      await axios.put(`${API_URL}/notification/mark-all-read`, {}, { headers });
      
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, read: true }))
      );
    } catch (err) {
      console.log(err);
    }
  };

  const deleteNotification = async (id) => {
    try {
      const token = localStorage.getItem('userToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      
      await axios.delete(`${API_URL}/notification/${id}`, { headers });
      
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    } catch (err) {
      console.log(err);
    }
  };

  const getFilteredNotifications = () => {
    switch (selectedFilter) {
      case 'unread':
        return notifications.filter(notification => !notification.read);
      case 'offers':
        return notifications.filter(notification => notification.type === 'offer');
      case 'messages':
        return notifications.filter(notification => notification.type === 'message');
      case 'auctions':
        return notifications.filter(notification => notification.type === 'auction');
      default:
        return notifications;
    }
  };

  const filteredNotifications = getFilteredNotifications();

  const getTypeConfig = (type) => {
    const configs = {
      'offer': { color: '#3b82f6', label: 'Offre' },
      'auction': { color: '#10b981', label: 'Enchère' },
      'message': { color: '#8b5cf6', label: 'Message' },
      'promo': { color: '#f59e0b', label: 'Promotion' },
      'shipping': { color: '#6366f1', label: 'Livraison' },
      'review': { color: '#ec4899', label: 'Avis' },
    };
    return configs[type] || { color: '#6b7280', label: 'Notification' };
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'À l\'instant';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'À l\'instant';
    } else if (diffMins < 60) {
      return `Il y a ${diffMins} min`;
    } else if (diffHours < 24) {
      return `Il y a ${diffHours} h`;
    } else if (diffDays < 7) {
      return `Il y a ${diffDays} j`;
    } else {
      return date.toLocaleDateString('fr-FR', { 
        day: 'numeric',
        month: 'short'
      });
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      'offer': 'DollarSign',
      'auction': 'Gavel',
      'message': 'MessageSquare',
      'promo': 'Gift',
      'shipping': 'Truck',
      'review': 'Star',
    };
    return icons[type] || 'Bell';
  };

  const LoadingSkeleton = () => (
    <div style={styles.loadingContainer}>
      <div style={styles.loadingHeader}>
        <div style={styles.loadingTitle}>
          <div style={{...styles.skeletonLine, width: '120px', height: '28px'}} />
          <div style={{...styles.skeletonLine, width: '80px', height: '16px', marginTop: '4px'}} />
        </div>
      </div>
      
      {[1, 2, 3, 4].map((item) => (
        <div key={item} style={styles.skeletonCard}>
          <div style={styles.skeletonIcon}>
            <div style={{...styles.skeletonCircle, width: '48px', height: '48px'}} />
          </div>
          <div style={styles.skeletonContent}>
            <div style={{...styles.skeletonLine, width: '70%', height: '16px'}} />
            <div style={{...styles.skeletonLine, width: '90%', height: '14px', marginTop: '8px'}} />
            <div style={{...styles.skeletonLine, width: '40%', height: '12px', marginTop: '12px'}} />
          </div>
        </div>
      ))}
    </div>
  );

  // Styles
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 20px',
      backgroundColor: '#fff',
      borderBottom: '1px solid #e5e7eb',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
      position: 'sticky',
      top: 0,
      zIndex: 100,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    backButton: {
      width: '40px',
      height: '40px',
      borderRadius: '20px',
      backgroundColor: '#f3f4f6',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
    },
    headerTitleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    headerTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#111827',
      margin: 0,
    },
    unreadBadgeContainer: {
      backgroundColor: '#ef4444',
      borderRadius: '12px',
      padding: '2px 8px',
      minWidth: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    unreadBadgeText: {
      color: '#fff',
      fontSize: '12px',
      fontWeight: '700',
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    markAllButton: {
      width: '40px',
      height: '40px',
      borderRadius: '20px',
      backgroundColor: '#f0f9ff',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
    },
    filterToggleButton: {
      width: '40px',
      height: '40px',
      borderRadius: '20px',
      backgroundColor: '#f3f4f6',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
    },
    filtersContainer: {
      backgroundColor: '#fff',
      borderBottom: '1px solid #e5e7eb',
      padding: '12px 0',
      position: 'sticky',
      top: '73px',
      zIndex: 99,
    },
    filtersContent: {
      padding: '0 20px',
      display: 'flex',
      gap: '8px',
      overflowX: 'auto',
    },
    filterButton: {
      padding: '8px 16px',
      backgroundColor: '#f3f4f6',
      borderRadius: '20px',
      border: '1px solid #e5e7eb',
      whiteSpace: 'nowrap',
      cursor: 'pointer',
    },
    filterButtonActive: {
      backgroundColor: '#3b82f6',
      borderColor: '#3b82f6',
    },
    filterText: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#6b7280',
      margin: 0,
    },
    filterTextActive: {
      color: '#fff',
    },
    filterCount: {
      fontSize: '12px',
      fontWeight: '500',
      color: '#9ca3af',
    },
    filterCountActive: {
      color: '#dbeafe',
    },
    content: {
      flex: 1,
    },
    // Styles pour le chargement
    loadingContainer: {
      backgroundColor: '#f8fafc',
    },
    loadingHeader: {
      padding: '16px 20px',
      backgroundColor: '#fff',
      borderBottom: '1px solid #e5e7eb',
    },
    loadingTitle: {
      display: 'flex',
      flexDirection: 'column',
    },
    skeletonCard: {
      backgroundColor: '#fff',
      borderRadius: '16px',
      padding: '16px',
      margin: '0 20px 12px',
      display: 'flex',
      alignItems: 'center',
      border: '1px solid #f3f4f6',
    },
    skeletonIcon: {
      marginRight: '16px',
    },
    skeletonCircle: {
      borderRadius: '24px',
      backgroundColor: '#f3f4f6',
    },
    skeletonContent: {
      flex: 1,
    },
    skeletonLine: {
      backgroundColor: '#f3f4f6',
      borderRadius: '6px',
    },
    notificationsList: {
      flex: 1,
      maxHeight: 'calc(100vh - 180px)',
      overflowY: 'auto',
    },
    notificationsContent: {
      padding: '20px',
    },
    notificationCard: {
      backgroundColor: '#fff',
      borderRadius: '20px',
      marginBottom: '12px',
      border: '1px solid #f3f4f6',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
    },
    unreadCard: {
      backgroundColor: '#f0f9ff',
      borderColor: '#dbeafe',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    },
    firstCard: {
      marginTop: '4px',
    },
    notificationTouchable: {
      padding: '16px',
      cursor: 'pointer',
      textDecoration: 'none',
      color: 'inherit',
    },
    notificationContent: {
      display: 'flex',
      alignItems: 'flex-start',
    },
    iconContainer: {
      width: '48px',
      height: '48px',
      borderRadius: '24px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: '16px',
    },
    iconText: {
      fontSize: '24px',
    },
    notificationBody: {
      flex: 1,
    },
    notificationHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '6px',
    },
    notificationTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#4b5563',
      flex: 1,
      margin: 0,
    },
    unreadTitle: {
      color: '#111827',
    },
    unreadIndicator: {
      width: '10px',
      height: '10px',
      borderRadius: '5px',
      backgroundColor: '#3b82f6',
      marginLeft: '8px',
    },
    notificationMessage: {
      fontSize: '14px',
      color: '#6b7280',
      lineHeight: '20px',
      marginBottom: '12px',
      margin: 0,
    },
    notificationFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    typeBadge: {
      padding: '4px 10px',
      borderRadius: '12px',
    },
    typeText: {
      fontSize: '12px',
      fontWeight: '600',
      margin: 0,
    },
    timeContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    timeText: {
      fontSize: '12px',
      color: '#9ca3af',
      margin: 0,
    },
    deleteButton: {
      marginLeft: '12px',
      padding: '4px',
      background: 'none',
      cursor: 'pointer',
    },
    emptyState: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '40px',
      minHeight: 'calc(100vh - 180px)',
    },
    emptyIconContainer: {
      width: '120px',
      height: '120px',
      borderRadius: '60px',
      backgroundColor: '#f3f4f6',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: '24px',
    },
    emptyIcon: {
      fontSize: '48px',
      margin: 0,
    },
    emptyTitle: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#111827',
      marginBottom: '8px',
      textAlign: 'center',
      margin: 0,
    },
    emptyMessage: {
      fontSize: '14px',
      color: '#6b7280',
      textAlign: 'center',
      lineHeight: '20px',
      marginBottom: '20px',
      margin: 0,
    },
    resetFilterButton: {
      padding: '10px 20px',
      backgroundColor: '#3b82f6',
      borderRadius: '10px',
      marginTop: '10px',
      cursor: 'pointer',
    },
    resetFilterText: {
      color: '#fff',
      fontWeight: '600',
      fontSize: '14px',
      margin: 0,
    },
    loader: {
      border: '3px solid #f3f4f6',
      borderTop: '3px solid #3b82f6',
      borderRadius: '50%',
      width: '24px',
      height: '24px',
      animation: 'spin 1s linear infinite',
      margin: '0 auto',
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button 
            style={styles.backButton}
            onClick={() => navigate(-1)}
          >
            <Icon name="ArrowLeft" size={24} color="#374151" />
          </button>
          <div style={styles.headerTitleContainer}>
            <h1 style={styles.headerTitle}>Notifications</h1>
            {unreadCount > 0 && (
              <div style={styles.unreadBadgeContainer}>
                <span style={styles.unreadBadgeText}>
                  {unreadCount}
                </span>
              </div>
            )}
          </div>
        </div>
        
        <div style={styles.headerRight}>
          {!loading && unreadCount > 0 && (
            <button 
              style={styles.markAllButton}
              onClick={markAllAsRead}
            >
              <Icon name="Check" size={20} color="#3b82f6" />
            </button>
          )}
          <button
            style={styles.filterToggleButton}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Icon name="Filter" size={20} color="#374151" />
          </button>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && !loading && (
        <div style={styles.filtersContainer}>
          <div style={styles.filtersContent}>
            {[
              { key: 'all', label: 'Toutes', count: notifications.length },
              { key: 'unread', label: 'Non lues', count: unreadCount },
              { key: 'offers', label: 'Offres', icon: 'DollarSign' },
              { key: 'messages', label: 'Messages', icon: 'MessageSquare' },
              { key: 'auctions', label: 'Enchères', icon: 'Gavel' },
            ].map((filter) => (
              <button
                key={filter.key}
                style={{
                  ...styles.filterButton,
                  ...(selectedFilter === filter.key ? styles.filterButtonActive : {})
                }}
                onClick={() => setSelectedFilter(filter.key)}
              >
                <span style={{
                  ...styles.filterText,
                  ...(selectedFilter === filter.key ? styles.filterTextActive : {})
                }}>
                  {filter.icon && <Icon name={filter.icon} size={14} style={{marginRight: '4px'}} />}
                  {filter.label}
                  {filter.count !== undefined && filter.count > 0 && (
                    <span style={{
                      ...styles.filterCount,
                      ...(selectedFilter === filter.key ? styles.filterCountActive : {})
                    }}>
                      {' '}({filter.count})
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Contenu principal */}
      {loading ? (
        <LoadingSkeleton />
      ) : (
        <div style={styles.content}>
          {filteredNotifications.length > 0 ? (
            <div style={styles.notificationsList}>
              <div style={styles.notificationsContent}>
                {filteredNotifications.map((notification, index) => {
                  const typeConfig = getTypeConfig(notification.type);
                  const typeIcon = getTypeIcon(notification.type);
                  
                  return (
                    <div
                      key={notification.id}
                      style={{
                        ...styles.notificationCard,
                        ...(!notification.read ? styles.unreadCard : {}),
                        ...(index === 0 ? styles.firstCard : {})
                      }}
                    >
                      <div
                        style={styles.notificationTouchable}
                        onClick={() => markAsRead(notification.id)}
                      >
                        <div style={styles.notificationContent}>
                          <div style={{
                            ...styles.iconContainer,
                            backgroundColor: `${typeConfig.color}15`
                          }}>
                            <Icon name={typeIcon} size={24} color={typeConfig.color} />
                          </div>
                          
                          <div style={styles.notificationBody}>
                            <div style={styles.notificationHeader}>
                              <h3 style={{
                                ...styles.notificationTitle,
                                ...(!notification.read ? styles.unreadTitle : {})
                              }}>
                                {notification.title}
                              </h3>
                              {!notification.read && (
                                <div style={styles.unreadIndicator} />
                              )}
                            </div>
                            
                            <p style={styles.notificationMessage}>
                              {notification.message}
                            </p>
                            
                            <div style={styles.notificationFooter}>
                              <div style={{
                                ...styles.typeBadge,
                                backgroundColor: `${typeConfig.color}10`
                              }}>
                                <span style={{
                                  ...styles.typeText,
                                  color: typeConfig.color
                                }}>
                                  {typeConfig.label}
                                </span>
                              </div>
                              
                              <div style={styles.timeContainer}>
                                <Icon name="Clock" size={12} color="#9ca3af" />
                                <span style={styles.timeText}>
                                  {formatTime(notification.createdAt)}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <button
                            style={styles.deleteButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                          >
                            <Icon name="X" size={18} color="#9ca3af" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyIconContainer}>
                <span style={styles.emptyIcon}>🔕</span>
              </div>
              <h2 style={styles.emptyTitle}>
                {selectedFilter === 'unread' 
                  ? "Toutes les notifications sont lues !" 
                  : "Aucune notification"
                }
              </h2>
              <p style={styles.emptyMessage}>
                {selectedFilter === 'unread'
                  ? "Vous avez tout vu. Revenez plus tard !"
                  : "Vous serez notifié dès qu'il y aura du nouveau."
                }
              </p>
              
              {selectedFilter !== 'all' && (
                <button
                  style={styles.resetFilterButton}
                  onClick={() => setSelectedFilter('all')}
                >
                  <span style={styles.resetFilterText}>Voir toutes les notifications</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        button {
          cursor: pointer;
          font-family: inherit;
          transition: opacity 0.2s;
        }
        
        button:hover {
          opacity: 0.8;
        }
        
        button:active {
          opacity: 0.9;
        }
        
        button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .notifications-list::-webkit-scrollbar {
          width: 6px;
        }
        
        .notifications-list::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }
        
        .notifications-list::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        
        .notifications-list::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        
        .filters-content::-webkit-scrollbar {
          height: 4px;
        }
        
        .filters-content::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 2px;
        }
        
        .filters-content::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 2px;
        }
      `}</style>
    </div>
  );
};

export default NotificationsScreen;